import { Stack, TextField, MenuItem, Slider, Typography } from '@mui/material'
import type { GranularityUnit } from '~/state/useGranularityState'
import { CONTROLS, GRANULARITY } from '~/components/Charts/chrome/chartChrome'
import { FY_GRANULARITY_ENABLED } from '~/util/fiscalYear'

interface Props {
  unit: GranularityUnit
  dots: number
  onUnitChange: (u: GranularityUnit) => void
  onDotsChange: (n: number) => void
}

const BASE_UNIT_KEYS = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
] as const satisfies readonly GranularityUnit[]

// NOTE: `satisfies` checks MEMBERSHIP, not exhaustiveness — adding a unit to GranularityUnit does
// NOT force it into this list. That is deliberate here: the picker is the OFFER, the zod enum is
// the PARSER, and FY is offered only where it differs from the calendar year.
const UNIT_KEYS: readonly GranularityUnit[] = FY_GRANULARITY_ENABLED
  ? [...BASE_UNIT_KEYS, 'fy']
  : BASE_UNIT_KEYS

export function GranularityControls({ unit, dots, onUnitChange, onDotsChange }: Props) {
  const label = dots === 1 ? GRANULARITY.unitSingular[unit] : GRANULARITY.unitPlural[unit]
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', flex: 1 }}>
      <TextField
        select
        size="small"
        label={CONTROLS.granularity}
        value={unit}
        onChange={(e) => onUnitChange(e.target.value as GranularityUnit)}
        sx={{ minWidth: 160 }}
      >
        {UNIT_KEYS.map((u) => (
          <MenuItem key={u} value={u}>
            {GRANULARITY.unitPlural[u]}
          </MenuItem>
        ))}
      </TextField>
      <Stack sx={{ minWidth: 160, flex: 1, maxWidth: 320 }}>
        <Slider
          value={dots}
          min={2}
          max={182}
          marks={[
            { value: 2 },
            { value: 4 },
            { value: 13 },
            { value: 26 },
            { value: 52 },
            { value: 182 },
          ]}
          onChange={(_, v) => onDotsChange(typeof v === 'number' ? v : v[0]!)}
          valueLabelDisplay="auto"
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap>
        {CONTROLS.perChartPanel(dots, label)}
      </Typography>
    </Stack>
  )
}
